import { ErroAction } from '@precad/compartilhado/cosmos/erroAction'
import { BASE_API } from './config'

/**
 * Cliente do proxy.
 *
 * Deliberadamente NÃO usa `criarClienteAction` do pacote compartilhado: aquele
 * fala o envelope do Cosmos, e esta página fala com a NOSSA Function. São
 * contratos diferentes, e fingir que são o mesmo esconderia justamente a
 * fronteira que protege a credencial.
 *
 * O contrato aqui é mínimo de propósito. A página envia só `data` e, quando a
 * rota pede, o token do Turnstile. Ela não escolhe a action do Cosmos nem monta
 * `meta` — isso é do proxy, e é o que impede o cliente forjar identidade.
 * A capability do rascunho viaja em cookie HttpOnly: o JavaScript nunca a vê.
 */

export interface OpcoesChamada {
  dados?: Record<string, unknown>
  turnstileToken?: string | null
  sinal?: AbortSignal
}

const TIMEOUT_MS = 30_000

export async function chamar<T = Record<string, unknown>>(
  rota: string,
  { dados, turnstileToken, sinal }: OpcoesChamada = {},
): Promise<T[]> {
  const corpo: Record<string, unknown> = { data: dados ?? {} }
  if (turnstileToken) corpo.turnstileToken = turnstileToken

  let resposta: Response
  try {
    resposta = await fetch(`${BASE_API}/${rota}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corpo),
      /* Mesma origem: o cookie HttpOnly do rascunho vai junto sem configuração,
         e não há preflight. */
      credentials: 'same-origin',
      signal: sinal ?? AbortSignal.timeout(TIMEOUT_MS),
    })
  } catch (erro) {
    const nome = (erro as Error)?.name
    if (nome === 'TimeoutError' || nome === 'AbortError') {
      throw new ErroAction('A operação demorou demais. Verifique sua conexão e tente novamente.', {
        codigo: 'TIMEOUT',
      })
    }
    throw new ErroAction('Não foi possível falar com o servidor. Verifique sua conexão.', { codigo: 'REDE' })
  }

  /* O proxy já entrega array de linhas limpo — o envelope do Cosmos
     (`CustomActionResponseContent`, wrappers OData, duplo-encode) morre lá.
     É por isso que esta página não importa o desembrulho do pacote
     compartilhado: ela não deveria nem saber que o backend é Cosmos Pro. */
  const texto = await resposta.text()
  let linhas: T[] = []
  if (texto.trim()) {
    try {
      const analisado: unknown = JSON.parse(texto)
      linhas = Array.isArray(analisado) ? (analisado as T[]) : [analisado as T]
    } catch {
      linhas = []
    }
  }

  /* O proxy responde 404 para rota fora da allowlist e 5xx para falha de
     plataforma. Recusa de NEGÓCIO chega em 200 com success 0 — tratada por
     `exigirSucesso`, não aqui. */
  if (!resposta.ok) {
    const primeira = linhas[0] as { message?: string; errorCode?: string } | undefined
    throw new ErroAction(primeira?.message ?? 'Não foi possível concluir a operação.', {
      status: resposta.status,
      codigo: primeira?.errorCode ?? null,
    })
  }

  return linhas
}

export interface ArquivoParaEnviar {
  arquivo: File
  papel: string
  socioId?: number | null
}

/**
 * Envio de documentos — único caminho multipart.
 *
 * O mapa arquivo→papel viaja em `data.arquivos` e é casado no servidor pelo
 * NOME do arquivo (a Fase 0 confirmou que `@Files.name` traz o nome real). Por
 * isso nomes repetidos no mesmo lote são recusados: o join ficaria ambíguo e o
 * documento iria para o papel errado, o que é pior do que falhar.
 */
export async function enviarArquivos<T = Record<string, unknown>>(
  rota: string,
  arquivos: ArquivoParaEnviar[],
  aoProgredir?: (fracao: number) => void,
): Promise<T[]> {
  if (!arquivos.length) throw new ErroAction('Nenhum arquivo para enviar.')

  const form = new FormData()
  form.append(
    'Content',
    JSON.stringify({
      data: {
        arquivos: arquivos.map((a) => ({
          nome: a.arquivo.name,
          papel: a.papel,
          socioId: a.socioId ?? null,
        })),
      },
    }),
  )
  form.append('ContentType', 'application/json')
  /* Content-Type NÃO é definido manualmente: o navegador precisa gerar o
     boundary do multipart. */
  for (const a of arquivos) form.append('arquivo', a.arquivo, a.arquivo.name)

  /* XHR em vez de fetch: só ele reporta progresso de UPLOAD, e numa conexão
     de balcão uma barra parada por 40 segundos parece travamento. */
  return new Promise<T[]>((resolver, rejeitar) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_API}/${rota}`)
    xhr.withCredentials = true
    xhr.timeout = 120_000

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && aoProgredir) aoProgredir(e.loaded / e.total)
    }
    xhr.ontimeout = () =>
      rejeitar(new ErroAction('O envio demorou demais. Verifique sua conexão e tente novamente.', { codigo: 'TIMEOUT' }))
    xhr.onerror = () =>
      rejeitar(new ErroAction('Não foi possível enviar os arquivos. Verifique sua conexão.', { codigo: 'REDE' }))

    xhr.onload = () => {
      let linhas: T[] = []
      if (xhr.responseText?.trim()) {
        try {
          const analisado: unknown = JSON.parse(xhr.responseText)
          linhas = Array.isArray(analisado) ? (analisado as T[]) : [analisado as T]
        } catch {
          linhas = []
        }
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const primeira = linhas[0] as { message?: string; errorCode?: string } | undefined
        rejeitar(
          new ErroAction(primeira?.message ?? 'Não foi possível enviar os arquivos.', {
            status: xhr.status,
            codigo: primeira?.errorCode ?? null,
          }),
        )
        return
      }
      resolver(linhas)
    }

    xhr.send(form)
  })
}

/** Lança quando a resposta é recusa de negócio; devolve a linha em caso de sucesso. */
export function exigirSucesso<T extends { success?: number | boolean; errorCode?: string; message?: string }>(
  linhas: T[],
): T {
  const linha = linhas[0]
  if (!linha) throw new ErroAction('Resposta vazia do servidor.')
  if (linha.success === 0 || linha.success === false) {
    throw new ErroAction(linha.message || 'Não foi possível concluir a operação.', {
      status: 200,
      codigo: linha.errorCode ?? null,
    })
  }
  return linha
}
